
import { Loader } from "@googlemaps/js-api-loader";
import * as Sentry from "@sentry/vue";
import _ from "lodash";
import Vue from "vue";
import { mapActions, mapGetters, mapMutations } from "vuex";
import areAllPropertiesFalse from "~/assets/utils/areAllPropertiesFalse";
import checkIfDetailUpdated from "~/assets/utils/checkIfDetailUpdated";
import highlightsDiff from "~/assets/utils/highlightsDiff";
import Modal from "~/components/Modal.vue";
import Notice from "~/components/Notice.vue";
import LargeSelect from "~/components/form/LargeSelect.vue";
import SmallRoundedSelect from "~/components/form/SmallRoundedSelect.vue";
import BoxCaseForm from "~/components/ship/BoxCaseForm.vue";
import RecentSearch from "~/components/ship/RecentSearch.vue";
import StageForm from "~/components/ship/StageForm.vue";
import { AVSSubStatus, AVSStatus } from "~/entities/AVS";
import Box from "~/entities/Box";
import { Details, StageDetails } from "~/entities/Details";
import {
  Category,
  Country,
  Insurance,
  Preset,
  Purpose,
  State,
  UserPreset,
} from "~/entities/Response/FormData";
import SavedAddress from "~/entities/SavedAddress";
import Trip from "~/entities/Trip";
import Units from "~/entities/Units";
import Validation from "~/entities/Validation";
import ValidationSearch from "~/entities/ValidationModules/ValidationSearch";

interface StageDetailsI {
  from: Details;
  to: Details;
}

export default Vue.extend({
  name: "ShipSearch",
  components: {
    SmallRoundedSelect,
    LargeSelect,
    StageForm,
    BoxCaseForm,
    RecentSearch,
    Notice,
    Modal,
  },
  layout: "WithServices",
  data() {
    return {
      validation: new Validation(new ValidationSearch()),
      stepNumber: 1,
      googleObj: null as any,
      outShip: this.$route.name !== "ship" ? true : (false as boolean),
      refreshView: false as boolean,
      defaultShipMode: [
        {
          id: 1,
          label: "One-Way",
        },
        {
          id: 2,
          label: "Round-Trip",
        },
        {
          id: 3,
          label: "Multi-City",
        },
      ],
      packageNumber: [
        {
          id: 1,
          label: "1 Box/Case",
        },
      ],
      units: [
        {
          id: 1,
          label: "in / lbs",
          units: new Units({
            weight: "lb",
            length: "in",
          }),
        },
        {
          id: 2,
          label: "cm / kgs",
          units: new Units({
            weight: "kg",
            length: "cm",
          }),
        },
      ],
      packagePresets: [] as Preset[],
      userPresets: [] as UserPreset[],
      userAddresses: [] as SavedAddress[],
      packageCategories: [] as Category[],
      insuranceOptions: [] as Insurance[],
      packagePurpose: [] as Purpose[],
      purposeSelected: null as any,
      allowedCountries: [] as Country[],
      allowedStates: [] as State[],
      currentUnit: {} as Units,
      currentShipMode: {
        id: 1,
        label: "One-Way",
      } as { id: number; label: string },
      currentCountries: {} as any,
      rendered: false as boolean,
      tripsLimit: 6,
      isOpen: false as boolean,
      avsChecked: false as boolean,
      stageDetails: [] as StageDetailsI[],
      updatedDetails: [] as StageDetailsI[],
      failedAvsEvent: [] as { from: boolean; to: boolean }[],
      changedFlags: [] as { from: any; to: any }[],
      disabled: false as boolean,
    };
  },
  async fetch() {
    const _this = this as any;
    await _this.fetchPackageCategories();
    await _this.fetchPackagePurpose();
    await _this.fetchPackagePresets();
    await _this.fetchInsuranceOptions();
    await _this.fetchAllowedCountries();
    await this.fetchAllowedStates();

    _this.packageCategories = _this.categories
      ?.map((el: Category, index: number) => {
        return {
          id: index as number,
          entryId: el.entryId as string,
          code: el.code as number,
          label: el.display as string,
          sortOrder: el.sortOrder as number,
        };
      })
      .sort((a, b) => {
        return a.sortOrder < b.sortOrder
          ? -1
          : a.sortOrder > b.sortOrder
          ? 1
          : 0;
      });
    _this.packagePresets = _this.presets
      ?.map((el: Preset) => {
        return {
          type: el.type as string,
          label: el.name as string,
          length: el.length as number,
          width: el.width as number,
          height: el.height as number,
          weight: el.weight as number,
          isEbike: el.isEbike as boolean,
          isExact: el.isExact as boolean,
          linearUnit: el.linearUnit as string,
          weightUnit: el.weightUnit as string,
          sortOrder: el.sortOrder as number,
        };
      })
      .sort((a, b) => {
        return a.sortOrder < b.sortOrder
          ? -1
          : a.sortOrder > b.sortOrder
          ? 1
          : 0;
      });
    _this.packagePurpose = _this.purposes
      ?.map((el: Purpose, index: number) => {
        return {
          id: index as number,
          entryId: el.entryId as string,
          code: el.code as number,
          label: el.display as string,
          sortOrder: el.sortOrder as number,
        };
      })
      .sort((a, b) => {
        return a.sortOrder < b.sortOrder
          ? -1
          : a.sortOrder > b.sortOrder
          ? 1
          : 0;
      });
    _this.insuranceOptions = _this.options
      ?.map((el: Insurance, index: number) => {
        return {
          id: index as number,
          entryId: el.entryId as string,
          label: el.display as string,
          coverage: el.coverage as number,
          cost: el.cost as number,
          sortOrder: el.sortOrder as number,
          afterText: el.afterText as string
        };
      })
      .sort((a, b) => {
        return a.coverage < b.coverage ? -1 : a.coverage > b.coverage ? 1 : 0;
      });
    _this.allowedCountries = _this.countries
      ?.map((el: Country) => {
        return {
          code: el.code as string,
          name: el.name as string,
          active: el.active as boolean,
          id: el.code as string,
          label: el.name as string,
        };
      })
      .filter((value) => value.active);
  },
  computed: {
    // Vuex getters
    ...mapGetters({
      activeNotices: "errorsBag/activeNotices" as any,
      alertResponses: "errorsBag/alertResponses" as any,
      loading: "loading",
      session: "user/session",
      boxes: "ship/boxes",
      trips: "ship/trips",
      currentUnitStore: "ship/units",
      details: "ship/details",
      purpose: "ship/purpose",
      ratesAreValid: "ship/ratesAreValid",
      eventInputOptions: "ship/eventInputOptions",
      shipMode: "ship/shipMode",
      currentEvent: "eventShipping/currentEvent",

      // formData
      categories: "formData/packageCategories",
      presets: "formData/packagePresets",
      purposes: "formData/packagePurpose",
      options: "formData/insuranceOptions",
      countries: "formData/allowedCountries",
      states: "formData/allowedStates",
      hasAdvancedInStep: "ship/hasAdvancedInStep",
    }),
    boxesCount(): number {
      return this.boxes ? this.boxes.length : (0 as number);
    },
    currentUnitIndex(): number {
      try {
        if (this.units && this.currentUnit)
          return this.units.findIndex(
            (unit) =>
              unit.units.length === this.currentUnit.length &&
              unit.units.weight === this.currentUnit.weight
          );
        return 0;
      } catch (e) {
        return 0;
      }
    },
    shipTypeSelection(): number {
      if (this.outShip) {
        return this.currentShipMode && this.currentShipMode.id !== 0
          ? this.currentShipMode.id - 1
          : 0;
      } else if (this.shipMode && this.shipMode.id === 1) {
        return 0;
      } else {
        return this.shipMode && this.shipMode.id > 2 ? 2 : 1;
      }
    },
    allAvsChecked(): boolean {
      return this.details.every((detail) => {
        const result =
          detail.from.avsChecked === true && detail.to.avsChecked === true;
        return result;
      });
    },
  },
  watch: {
    boxes: {
      deep: true,
      immediate: true,
      handler() {
        this.rendered = false;
        this.rendered = true;
      },
    },
    activeNotices() {
      this.rendered = false;
      this.rendered = true;
    },
    ratesAreValid(newVal) {
      if (newVal) {
        if (newVal.valid) {
          Sentry.addBreadcrumb({
            message: "Threshold: requested Rates are valid",
            data: { rates: JSON.stringify(newVal) },
            category: "search",
          });
        } else {
          this.setError(newVal, "rates");
          Sentry.addBreadcrumb({
            message: "Threshold: requested Rates are NOT valid",
            data: { rates: JSON.stringify(newVal) },
            category: "search",
          });
        }
      }
    },
    trip: {
      immediate: true,
      handler() {
        (this as any).validation.module.validateGoogleSelection();
      },
    },
    session: {
      deep: true,
      immediate: true,
      async handler(newVal: any) {
        if (newVal && newVal.sessionType) {
          // when season logs, call to fill the user presets
          await this.getUserPresets();
        } else if (newVal) {
          this.userPresets = [] as any[];
          this.userAddresses = [] as any[];
        }
      },
    },
    StageDetails: {
      deep: true,
      immediate: true,
      handler() {
        this.validation.Validate();
      },
    },
    // listen for user request and fill the packagePresets and address
  },
  mounted() {
    this.setLoading(true);
    this.setOutShip(this.outShip);
    this.initMap();
    for (let i = 2; i <= 25; i++) {
      this.packageNumber.push({
        id: i,
        label: i + " Boxes/Cases",
      });
    } // set default package number
    this.currentShipMode =
      this.currentEvent && this.currentEvent.shipType
        ? this.defaultShipMode[this.currentEvent.shipType - 1]
        : this.defaultShipMode[0]; // set default ship mode
    setTimeout(() => {
      if (this.trips && !this.trips.length) {
        const trip: Trip[] = [];
        trip.push(new Trip());
        if (this.currentEvent && this.currentEvent.shipType) {
          for (let index = 0; index < this.currentEvent.shipType - 1; index++) {
            trip.push(new Trip());
          }
        }
        this.setTrips(trip);
      }
      if (typeof window !== "undefined" && window.gtag) {
        const route = this.$route.name;
        let event = null;
        if (
          route === "events-event" ||
          route === "event" ||
          route === "partners-event"
        ) {
          event = this.currentEvent?.name
            ? this.currentEvent.name
            : this.$route.params.event;
        }
        try {
          window.gtag("event", "page_impression", {
            pagePath: this.$route.path,
            pageTitle: event
              ? "Ship - " + event + " - Search"
              : "Ship - Search",
            visitorType: this.$auth.loggedIn ? "customer" : "guest",
          });
        } catch (e) {
          console.log(e);
        }
      }
    }, 100); // set default trips
    if (this.boxes && !this.boxes.length) {
      const boxes: Box[] = [];
      boxes.push(new Box());
      this.setBoxes(boxes);
    } // set default boxes
    // set default units
    if (this.units) {
      this.currentUnit = this.currentUnitStore ?? this.units[0].units; // set default unit
    } // set default units

    if (this.purpose && !this.purposeSelected) {
      this.purposeSelected = this.purpose;
    } // set default purpose
    // set component for validation module
    (this as any).validation.module.component = this;
    window.scrollTo(0, 0);

    // Used to restore searches that are coming from yoursearches page
    if (this.$route.params?.search) {
      const savedSearch = this.$route.params?.search;
      this.$set(this.$route.params, "search", false);
      this.onSearchResumed({ index: 0, search: savedSearch });
    }
    if (this.details?.length) {
      this.stageDetails = _.cloneDeep(this.details);
      this.updatedDetails = _.cloneDeep(this.details);
      if (this.hasAdvancedInStep) {
        this.validation.Validate();
      }
    }
    this.setLoading(false);
  },
  methods: {
    // Vuex actions
    ...mapMutations({
      setLoading: "setLoading",
      setBoxes: "ship/setBoxes",
      removeBoxes: "ship/removeBoxes",
      setTrips: "ship/setTrips",
      removeTrips: "ship/removeTrips",
      setUnits: "ship/setUnits",
      setPurpose: "ship/setPurpose",
      setShipMode: "ship/setShipMode",
      setRates: "ship/setRates",
      cleanBoxErrors: "ship/cleanBoxErrors",
      setBoxError: "ship/setBoxError",
      addTrip: "ship/addTrip",
      removeTripByIndex: "ship/removeTripByIndex",
      setOutShip: "ship/setOutShip",
      reassignActiveSearch: "user/reassignActiveSearch",
      setBoxProperty: "ship/setBoxProperty",
      setDetails: "ship/setDetails",
      setHasAdvancedInStep: "ship/setHasAdvancedInStep",
    }),
    ...mapActions({
      pushBox: "ship/pushBox",
      getRates: "ship/getRates",
      requestAlert: "errorsBag/requestAlert",
      clearActiveNotices: "errorsBag/clearActiveNotices",
      clearAlertResponses: "errorsBag/clearAlertResponses",
      removeNotice: "errorsBag/removeNotice",
      addNotice: "errorsBag/addNotice",
      requestRates: "ship/requestRates",
      setStep: "ship/setStep",
      forgetSearch: "user/forgetSearch",
      resumeSearch: "user/resumeSearch",
      logRequest: "ship/logRequest",
      requestAVSCheck: "ship/requestAVSCheck",
      // formData
      requestUserPresets: "user/requestUserPresets",
      fetchPackageCategories: "formData/fetchPackageCategories",
      fetchPackagePresets: "formData/fetchPackagePresets",
      fetchPackagePurpose: "formData/fetchPackagePurpose",
      fetchInsuranceOptions: "formData/fetchInsuranceOptions",
      fetchAllowedCountries: "formData/fetchAllowedCountries",
      fetchAllowedStates: "formData/fetchAllowedStates",
    }),
    highlightsDiff,
    async initMap() {
      // Initialize
      const gmapLoader: Loader = new Loader({
        apiKey: this.$config.mapsKey,
        libraries: ["places"],
        language: "en",
      });
      return await gmapLoader
        .load()
        .then((google: any) => {
          this.googleObj = google;
        })
        .catch(() => {});
    },
    addTrip() {
      this.rendered = false;
      if (this.trips.length < this.tripsLimit) {
        this.setTrips([...this.trips, new Trip()]);
      }
      (this as any).validation.module.validateStages();
      this.rendered = true;
    },
    removeTrip(index?: number): void {
      this.setLoading(true);
      this.rendered = false;
      if (!index && index === undefined) {
        if (this.trips.length >= 3) {
          this.removeTrips();
          (this as any).validation.module.validateStages();
        } else {
          this.removeTrips();
        }
        setTimeout(() => {
          this.setLoading(false);
          this.rendered = true;
        }, 500);
        return;
      } else if (this.trips.length >= 3) {
        this.removeTripByIndex(index);
        (this as any).validation.module.validateStages();
        setTimeout(() => {
          this.setLoading(false);
          this.rendered = true;
        }, 500);
        return;
      }
      this.rendered = true;
    },
    onChangeUnits(unit: number) {
      this.currentUnit = this.units.find((el) => el.id === unit)!.units;
      this.setUnits(this.currentUnit);
    },
    onChangeShipMode(shipMode: number): void {
      this.rendered = false;
      this.currentShipMode = this.defaultShipMode.find(
        (el) => el.id === shipMode
      )!;
      const minTrips = this.currentShipMode.id;
      this.setShipMode(this.currentShipMode);
      const trips = this.trips;
      // this.setTrips([]);
      if (trips.length < minTrips) {
        for (let i = trips.length; i < minTrips; i++) {
          this.addTrip();
        }
      } else if (trips.length > minTrips) {
        for (let i = trips.length; i > minTrips; i--) {
          this.removeTrip();
        }
      }
      this.rendered = true;
    },
    onChangeBoxes(boxes: number) {
      const nBoxes = boxes;
      if (this.boxes.length < nBoxes) {
        for (let i = this.boxes.length; i < nBoxes; i++) {
          this.setBoxes([...this.boxes, new Box()]);
        }
      } else if (this.boxes.length > nBoxes) {
        for (let i = this.boxes.length; i > nBoxes; i--) {
          this.removeBoxes();
        }
      }
    },
    onCountryChanged(country: any) {
      const index = country.index;
      this.currentCountries[index] = country.shortName;
      (this as any).validation.module.validateBoxes();
      (this as any).validation.module.validateStages();
    },
    purposeSelectionMade(selection: any) {
      if (selection) {
        this.purposeSelected = selection.option;
      } else {
        this.purposeSelected = false;
      }
    },
    setError(e, type: any = "") {
      // example: called from stageForm e = {direction: 'from' || 'to', country: 'Russia'}, type = 'country'
      // call to request error on validation object
      (this as any).validation.module.requestAlert({ event: e, type });
    },
    clearError(e, type: any = "") {
      // example: called from stageForm e = 'from' || 'to', type = 'country'
      (this as any).validation.module.removeNotice({
        event: { direction: e },
        type,
      });
      // call to clean individual error on validation object
    },
    validateBoxes() {
      // call validate boxes in validation object
      (this as any).validation.module.validateBoxes();
    },
    async validateStep() {
      await new Promise((resolve) => {
        setTimeout(() => {
          resolve(false);
        }, 100);
      });
      if (await (this as any).validation.Validate()) {
        // Reassign to clean any active search
        this.setLoading(true);
        this.reassignActiveSearch();
        if (!this.allAvsChecked) {
          // show modal
          const needsAVSModal = this.checkIfNeededAVSModal();
          await new Promise((resolve) => {
            setTimeout(() => {
              resolve(false);
            }, 1000);
          });
          if (needsAVSModal) {
            this.setLoading(false);
            this.isOpen = true;
            this.markAvsChecked();
            return;
          } else {
            this.markAvsChecked();
            this.setDetails(this.stageDetails);
          }
        }
        this.logRequest(window ? window.navigator.userAgent : "");
        this.requestRates({ stepName: "Costs", stepNumber: 2 });
      }
    },
    onSearchAbandoned(data) {
      this.forgetSearch(data);
    },
    onSearchResumed(data) {
      this.currentUnit = data.search.units;
      this.$nextTick(() => this.resumeSearch(data));
      this.logRequest(window ? window.navigator.userAgent : "");
    },
    async getUserPresets() {
      try {
        const _this = this as any;
        const response = await _this.requestUserPresets();
        if (response) {
          if (response.userPackages) {
            _this.userPresets = response.userPackages
              .map((el: UserPreset) => {
                // sort boxes
                return {
                  label: el.name,
                  length: el.length,
                  width: el.width,
                  height: el.height,
                  weight: el.weight,
                  isEbike: el.isEbike,
                  linearUnit: el.linearUnit,
                  weightUnit: el.weightUnit,
                  hasBattery: el.hasBattery,
                  coverage: el.coverage,
                  description: el.description,
                  outsideDescription: el.outsideDescription,
                  categoryCode: el.categoryCode,
                  marketValue: el.marketValue,
                };
              })
              .sort((a, b) => {
                return a.sortOrder < b.sortOrder
                  ? -1
                  : a.sortOrder > b.sortOrder
                  ? 1
                  : 0;
              });
          }
          if (response.userAddresses) {
            // no need to sort addresses
            _this.userAddresses = response.userAddresses;
          }
        } else {
          _this.userPresets = [] as UserPreset[];
          _this.userAddresses = [] as SavedAddress[];
        }
      } catch (e) {
        console.log(e);
      }
    },
    setUpdatedDetails(
      {
        updatedDetails,
        direction,
        changedFlags,
      }: {
        updatedDetails: StageDetailsI;
        direction: string;
        changedFlags: any;
      },
      index: number
    ) {
      if (!this.updatedDetails?.[index]) {
        this.updatedDetails[index] = { from: new Details(), to: new Details() };
      }

      if (!this.changedFlags[index]) {
        this.changedFlags[index] = { from: {}, to: {} };
      }
      this.changedFlags[index][direction] = changedFlags;
      this.updatedDetails[index][direction] = updatedDetails;
    },
    hideModal() {
      this.isOpen = false;
      this.validation.module.validating = false;
      this.avsChecked = true;
      this.markAvsChecked();
      this.setRejectedEnumState();
      this.setDetails(this.stageDetails);
      this.requestAlert({ name: "warningAVSFailed", type: "address" });
    },
    setRejectedEnumState() {
      for (const stageDetail of this.stageDetails) {
        if (!stageDetail.from.isSavedAddress && !stageDetail.from.passedAvs) {
          stageDetail.from.avsStatus.status = AVSStatus.failed;
          stageDetail.from.avsStatus.subStatus = AVSSubStatus.rejectedByUser;
        }
        if (!stageDetail.to.isSavedAddress && !stageDetail.to.passedAvs) {
          stageDetail.to.avsStatus.status = AVSStatus.failed;
          stageDetail.from.avsStatus.subStatus = AVSSubStatus.rejectedByUser;
        }
      }
    },
    completeModal() {
      if (this.isOpen) {
        this.setLoading(true);
        this.updateStage(true);
        this.isOpen = false;
        const stageDetailsUpdated = _.cloneDeep(this.stageDetails);
        for (const stageDetail of stageDetailsUpdated) {
          stageDetail.from.avsChecked = true;
          stageDetail.to.avsChecked = true;
        }
        this.setDetails(stageDetailsUpdated);
        this.logRequest(window ? window.navigator.userAgent : "");
        this.requestRates({ stepName: "Costs", stepNumber: 2 });
      }
      (this as any).validation.module.validating = false;
    },
    updateStage(avsFailed = true) {
      // here, check here if it is being added
      const updateProperty = (source, target, property) => {
        if (typeof source[property] === "string") {
          target[property] = source[property].trim();
        } else if (source[property] !== undefined) {
          target[property] = source[property];
        }
      };
      for (let step = 0; step < (this as any).stageDetails.length; step++) {
        const stageDetail = this.stageDetails[step];
        const updatedDetail = this.updatedDetails[step];
        const properties = [
          "company",
          "streetAddress",
          "address1",
          "address2",
          "address3",
          "city",
          "state",
          "postalCode",
          "countryCode",
          "isResidential",
          "success",
          "passedAvs",
          "errorCode",
          "errorMessage",
          "isChanged",
          "validationLevel",
          "validationSource",
          "avsStatus",
          "avsSubStatus",
          "avsStatus",
        ];
        if (avsFailed && stageDetail && updatedDetail) {
          ["from", "to"].forEach((key) => {
            const isValidSavedAddress =
              stageDetail[key].isSavedAddress && stageDetail[key].passedAvs;
            const isValidEventAddress =
              stageDetail[key].isEventAddress && stageDetail[key].passedAvs;
            if (
              stageDetail[key] &&
              updatedDetail[key] &&
              !isValidSavedAddress &&
              !isValidEventAddress
            ) {
              properties.forEach((property) =>
                updateProperty(updatedDetail[key], stageDetail[key], property)
              );
            }
          });
        } else if (updatedDetail.from && stageDetail.from) {
          properties.forEach((property) =>
            updateProperty(stageDetail.from, updatedDetail.from, property)
          );
        }
      }
    },
    mapStates(states: Array<State>) {
      try {
        const _this = this;
        for (let step = 0; step < states.length; step++) {
          const country = states[step].country;
          const countryStates = _this.allowedStates[country];
          if (
            typeof countryStates === "undefined" ||
            (countryStates && countryStates.length === 0)
          ) {
            _this.allowedStates[country] = [];
          }
          const state = {} as State;
          Object.assign(state, states[step]);
          state.id = state.stateprovcode;
          state.label = state.stateprovcode + " - " + state.stateprov;
          _this.allowedStates[country].push(state);
        }
        return true;
      } catch (e) {
        return false;
      }
    },
    markAvsChecked() {
      this.stageDetails = _.cloneDeep(this.details);
      for (const stageDetail of this.stageDetails) {
        stageDetail.from.avsChecked = true;
        stageDetail.to.avsChecked = true;
      }
    },
    checkIfNeededAVSModal(): boolean {
      let needsAVSModal = false;
      for (let i = 0; i < this.details.length; i++) {
        if (
          this.details[i].from.streetAddress ||
          this.details[i].from.address1
        ) {
          if (!areAllPropertiesFalse(this.changedFlags?.[i]?.from)) {
            needsAVSModal = true;
          }
        }
        if (this.details[i].to.streetAddress || this.details[i].to.address1) {
          if (!areAllPropertiesFalse(this.changedFlags?.[i]?.to)) {
            needsAVSModal = true;
          }
        }
      }
      return needsAVSModal;
    },
    disabledContinueBtn(event) {
      this.disabled = event;
    },

    checkIfDetailUpdated,
    areAllPropertiesFalse,
  },
  fetchOnServer: false,
});
